import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { validateEmailParts } from '../lib/contactValidation';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AuthShell from '../components/layout/AuthShell';

const SUPER_USER_API_URL = process.env.REACT_APP_BACKEND_URL;

function SuperUserLoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const emailCheck = validateEmailParts(formData.email);
    if (!emailCheck.valid) {
      newErrors.email =
        emailCheck.error === 'emailRequired' ? 'Email is required' : 'Enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${SUPER_USER_API_URL}/api/super-user/login`, formData);

      localStorage.setItem('super_access_token', response.data.access_token);

      toast.success('Super user login successful');
      navigate('/super-user/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      setErrors({ general: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Administrative Access"
      description="Sign in to manage subscribers and billing"
      footer={(
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            Regular user access?{' '}
            <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
              Go to standard login
            </Link>
          </p>
          <p className="text-xs text-muted-foreground">
            Restricted access. Unauthorized attempts are logged and monitored.
          </p>
        </div>
      )}
    >
      <div className="mb-6 flex items-center gap-2 text-sm font-medium text-brand-600">
        <Shield className="h-5 w-5" />
        Super User Portal
      </div>

      {errors.general && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>{errors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="superuser@omnichek.com"
            required
            disabled={loading}
          />
          {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              className="pr-11"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-muted-foreground hover:text-foreground"
              disabled={loading}
              aria-label="Toggle visibility"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
        </div>

        <Button type="submit" disabled={loading} className="w-full">
          {loading ? 'Signing in…' : 'Sign In as Super User'}
        </Button>
      </form>
    </AuthShell>
  );
}

export default SuperUserLoginPage;
